import React, { useMemo, useState } from 'react';
import { amActions } from '../amStore';
import { COLORS, VEHICLE_CAPACITY } from '../amConfig';
import { monthLabel } from '../amRules';
import { ftlTrips, utilization, groupBy } from '../amMetrics';
import {
  Panel, Seg, KpiCard, Chart, clickTo, SortTable, DrillTable, MultiSelect, RankedBars, fmtInt, fmtNum, fmtPct, ragColor,
} from '../amUi';

const LANE_LEVELS = [
  { field: 'srcCity', label: 'Origin', filterKey: 'origin' },
  { field: 'dstCity', label: 'Destination', filterKey: 'dstCity' },
  { field: 'lane', label: 'Lane', filterKey: 'lane' },
  { field: 'transporter', label: 'Transporter', filterKey: 'transporter' },
  { field: 'vehicle', label: 'Vehicle type', filterKey: 'vehicle' },
];
const BREAKDOWNS = [
  { value: 'entity', label: 'Entity' }, { value: 'transporter', label: 'Transporter' },
  { value: 'vehicle', label: 'Vehicle type' }, { value: 'lane', label: 'Lane' },
];

const t = (kg) => kg / 1000;
const scrollToId = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };
const utilCell = (v, missingText = 'No capacity') => {
  if (v === null) return <span className="muted">{missingText}</span>;
  if (v > 100) {
    return <b className="am-over" title="Above 100% of rated capacity - check the weight, CBM or vehicle type on these trips">⚠ {fmtPct(v)}</b>;
  }
  return <b style={{ color: ragColor(v) }}>{fmtPct(v)}</b>;
};

/** Trip-grain stats for drill / breakdown tables. */
const tripStats = (trips, field) => [...groupBy(trips, field).entries()]
  .map(([name, items]) => ({ name, ...utilization(items) }))
  .sort((a, b) => b.trips - a.trips);

const tripColumns = (label) => [
  { key: 'name', label, render: (r) => <span className="am-name">{r.name}</span> },
  { key: 'trips', label: 'Trips', align: 'right', bar: true, render: (r) => fmtInt(r.trips) },
  { key: 'records', label: 'Shipments', align: 'right', render: (r) => fmtInt(r.records) },
  { key: 'weight', label: 'Weight (t)', align: 'right', render: (r) => fmtNum(t(r.weight)) },
  { key: 'weightUtil', label: 'Weight util %', align: 'right', render: (r) => utilCell(r.weightUtil) },
  { key: 'volUtil', label: 'Volume util %', align: 'right', render: (r) => utilCell(r.volUtil, 'Data unavailable') },
];

export default function FtlPage({ ctx }) {
  const { rows, drillPath, drillToExplorer } = ctx;
  const openFtl = () => drillToExplorer({ type: ['FTL'] });
  const trips = useMemo(() => ftlTrips(rows), [rows]);
  const u = useMemo(() => utilization(trips), [trips]);
  const months = useMemo(() => [...new Set(trips.map((x) => x.month).filter(Boolean))].sort(), [trips]);
  const byMonth = useMemo(() => {
    const g = groupBy(trips, 'month');
    return months.map((m) => ({ m, ...utilization(g.get(m) || []) }));
  }, [trips, months]);

  const vehicleRows = useMemo(() => tripStats(trips, 'vehicle').map((r) => {
    const cap = VEHICLE_CAPACITY[r.name];
    return { ...r, capKg: cap ? cap.kg : null, capCbm: cap ? cap.cbm : null };
  }), [trips]);

  const topLanes = useMemo(() => tripStats(trips, 'lane').slice(0, 15), [trips]);
  const laneBars = useMemo(() => topLanes.map((l) => ({
    name: l.name, total: l.trips,
    note: l.weightUtil === null ? 'No capacity' : `${fmtPct(l.weightUtil)} util`,
    extra: [`Weight: ${fmtNum(t(l.weight))} t`, `Weight util: ${l.weightUtil === null ? 'No capacity' : fmtPct(l.weightUtil)} (${fmtInt(l.capTrips)} trips with capacity)`],
    util: l.weightUtil,
  })), [topLanes]);

  const weightTripsChart = useMemo(() => ({
    xAxis: { categories: months.map(monthLabel) },
    yAxis: [{ title: { text: 'Weight (t)' } }, { title: { text: 'Trips' }, opposite: true }],
    plotOptions: { series: clickTo((cat, pt) => amActions.toggle('month', months[pt.index])) },
    tooltip: { shared: true, valueDecimals: 0 },
    series: [
      { type: 'column', name: 'Weight (t)', color: COLORS.steel[2], data: byMonth.map((x) => +t(x.weight).toFixed(1)) },
      { type: 'line', name: 'Trips', yAxis: 1, color: COLORS.navy, data: byMonth.map((x) => x.trips), marker: { radius: 4 } },
    ],
  }), [byMonth, months]);

  // Transporter-wise monthly trips with transporter selection (top 5 by default).
  const tptAll = useMemo(() => tripStats(trips, 'transporter'), [trips]);
  const [pickTpt, setPickTpt] = useState([]);
  const shownTpt = pickTpt.length ? pickTpt : tptAll.slice(0, 5).map((x) => x.name);
  const tptChart = useMemo(() => {
    const g = groupBy(trips, (x) => `${x.transporter}|${x.month}`);
    return {
      chart: { type: 'line' },
      xAxis: { categories: months.map(monthLabel) },
      yAxis: { title: { text: 'Trips' } },
      plotOptions: { series: clickTo((cat, pt) => amActions.toggle('transporter', pt.series.name)) },
      tooltip: { shared: true, footerFormat: '<span style="font-size:10px;color:#6b778c">Click a line to filter to that transporter</span>' },
      series: shownTpt.map((name, i) => ({ name, color: COLORS.steel[i % COLORS.steel.length], data: months.map((m) => (g.get(`${name}|${m}`) || []).length), marker: { radius: 3 } })),
    };
  }, [trips, months, shownTpt.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  const vehChart = useMemo(() => {
    const top = tripStats(trips, 'vehicle').slice(0, 6).map((x) => x.name);
    const key = (x) => (top.includes(x.vehicle) ? x.vehicle : 'Others');
    const g = groupBy(trips, (x) => `${key(x)}|${x.month}`);
    const names = [...top, ...(trips.some((x) => !top.includes(x.vehicle)) ? ['Others'] : [])];
    return {
      chart: { type: 'column' },
      xAxis: { categories: months.map(monthLabel) },
      yAxis: { title: { text: 'Trips' } },
      plotOptions: {
        column: { stacking: 'normal' },
        series: clickTo((cat, pt) => (pt.series.name === 'Others' ? amActions.toggle('month', months[pt.index]) : amActions.toggle('vehicle', pt.series.name))),
      },
      tooltip: { shared: true, footerFormat: '<span style="font-size:10px;color:#6b778c">Click a segment to filter to that vehicle type</span>' },
      series: names.map((n, i) => ({ name: n, color: n === 'Others' ? COLORS.grey : COLORS.steel[i % COLORS.steel.length], data: months.map((m) => (g.get(`${n}|${m}`) || []).length) })),
    };
  }, [trips, months]);

  const utilChart = useMemo(() => ({
    xAxis: { categories: months.map(monthLabel) },
    yAxis: [{ title: { text: 'Weight (t)' } }, { title: { text: 'Utilization %' }, opposite: true, max: 100, endOnTick: false, min: 0, labels: { format: '{value}%' } }],
    plotOptions: { series: clickTo((cat, pt) => amActions.toggle('month', months[pt.index])) },
    tooltip: { shared: true },
    series: [
      { type: 'column', name: 'Weight (t)', color: COLORS.steel[4], data: byMonth.map((x) => +t(x.weight).toFixed(1)) },
      { type: 'line', name: 'Weight util %', yAxis: 1, color: COLORS.navy, data: byMonth.map((x) => (x.weightUtil === null ? null : +x.weightUtil.toFixed(1))), tooltip: { valueSuffix: '%' } },
      { type: 'line', name: 'Volume util %', yAxis: 1, color: COLORS.steel[2], dashStyle: 'ShortDash', data: byMonth.map((x) => (x.volUtil === null ? null : +x.volUtil.toFixed(1))), tooltip: { valueSuffix: '%' } },
    ],
  }), [byMonth, months]);

  const [breakdown, setBreakdown] = useState('transporter');
  const breakdownRows = useMemo(() => tripStats(trips, breakdown), [trips, breakdown]);

  if (!trips.length) {
    return <Panel title="FTL utilization"><div className="am-empty">No FTL trips under the current filters.</div></Panel>;
  }

  return (
    <>
      <div className="am-kpis six">
        <KpiCard label="Trips" value={fmtInt(u.trips)} sub="Vehicle + dispatch date + transporter" onClick={openFtl} hint="Open FTL shipments" />
        <KpiCard label="FTL shipment records" value={fmtInt(u.records)} sub="LR records on FTL trips" onClick={openFtl} hint="Open FTL shipments" />
        <KpiCard label="Weight transported" unit="t" value={fmtInt(t(u.weight))} sub="Heaviest loads first" onClick={() => drillToExplorer({ type: ['FTL'] }, { sort: 'weight' })} hint="Open FTL shipments, heaviest first" />
        <KpiCard label="Trips with capacity" value={fmtInt(u.capTrips)} sub={`${fmtInt(u.trips - u.capTrips)} with no capacity master`} onClick={() => scrollToId('am-ftl-vehicles')} hint="Jump to the vehicle type table" />
        <KpiCard label="Weight utilization" unit="%" value={fmtPct(u.weightUtil)} accent={ragColor(u.weightUtil)} sub={`${fmtInt(t(u.utilWeight))} t of ${fmtInt(t(u.capWeight))} t capacity`} onClick={() => scrollToId('am-ftl-vehicles')} hint="Jump to utilization by vehicle type" />
        <KpiCard label="Volume utilization" unit="%" value={fmtPct(u.volUtil)} accent={ragColor(u.volUtil)} sub={`${fmtInt(u.volTrips)} trips with valid CBM`} onClick={() => scrollToId('am-ftl-util')} hint="Jump to monthly utilization" />
      </div>

      <Panel id="am-ftl-vehicles" title="Utilization by vehicle type" subtitle="Click a vehicle type to filter · types without a capacity master show “No capacity”, never 0%">
        <SortTable rows={vehicleRows} defaultSort={{ key: 'trips', dir: 'desc' }}
          onRowClick={(r) => amActions.toggle('vehicle', r.name)}
          columns={[
            { key: 'name', label: 'Vehicle type', render: (r) => <span className="am-name">{r.name}</span> },
            { key: 'trips', label: 'Trips', align: 'right', bar: true, render: (r) => fmtInt(r.trips) },
            { key: 'capKg', label: 'Capacity / vehicle (t · CBM)', align: 'right', render: (r) => (r.capKg ? `${fmtNum(t(r.capKg))} t · ${fmtNum(r.capCbm)} CBM` : <span className="muted">No capacity</span>) },
            { key: 'weight', label: 'Weight (t)', align: 'right', render: (r) => fmtNum(t(r.weight)) },
            { key: 'capWeight', label: 'Total capacity (t)', align: 'right', render: (r) => (r.capKg ? fmtNum(t(r.capWeight)) : '–') },
            { key: 'utilWeight', label: 'Utilized weight (t)', align: 'right', render: (r) => (r.capKg ? fmtNum(t(r.utilWeight)) : '–') },
            { key: 'weightUtil', label: 'Weight util %', align: 'right', render: (r) => utilCell(r.weightUtil) },
            { key: 'volUtil', label: 'Volume util %', align: 'right', render: (r) => utilCell(r.volUtil, r.capKg ? 'Data unavailable' : 'No capacity') },
          ]} />
      </Panel>

      <div className="am-grid two">
        <Panel title="Top 15 lanes by trips" subtitle="Bar colour = weight utilization (red below 85%, green 85%+, grey = no capacity) · click to filter">
          <RankedBars items={laneBars} valueLabel="trips" selected={ctx.s.filters.lane} onSelect={(v) => amActions.toggle('lane', v)}
            colorOf={(it) => (it.util === null ? COLORS.grey : ragColor(it.util))} />
        </Panel>
        <Panel title="Lane-wise utilization" subtitle="Origin → destination → lane → transporter → vehicle type → shipments">
          <DrillTable rows={trips} levels={LANE_LEVELS} statsFn={tripStats} columnsFor={tripColumns} sortKey="trips"
            onLeaf={(path) => drillPath(path, { type: ['FTL'] })} />
        </Panel>
      </div>

      <div className="am-grid two">
        <Panel title="Monthly weight vs trips"><Chart options={weightTripsChart} height={270} /></Panel>
        <Panel title="Monthly trips by vehicle type" subtitle="Top 6 types + others"><Chart options={vehChart} height={270} /></Panel>
      </div>

      <Panel title="Monthly trips by transporter"
        actions={<MultiSelect label="Transporters" selected={pickTpt} onChange={setPickTpt}
          getOptions={() => tptAll.map((x) => ({ value: x.name, label: x.name, count: x.trips }))} />}>
        <Chart options={tptChart} height={260} />
      </Panel>

      <Panel id="am-ftl-util" title="Weight vs utilization by month"
        actions={<Seg size="sm" value={breakdown} onChange={setBreakdown} options={BREAKDOWNS} />}>
        <Chart options={utilChart} height={270} />
        <SortTable columns={tripColumns(BREAKDOWNS.find((b) => b.value === breakdown).label)} rows={breakdownRows}
          defaultSort={{ key: 'trips', dir: 'desc' }} maxRows={10}
          onRowClick={(r) => amActions.toggle({ entity: 'entity', transporter: 'transporter', vehicle: 'vehicle', lane: 'lane' }[breakdown], r.name)} />
      </Panel>
      <p className="am-note">A trip is Vehicle No + dispatch date + transporter, and takes the lane, transporter and vehicle type of its heaviest row. Weight utilization uses trips whose vehicle type has a capacity; volume utilization uses only trips where every row has a valid CBM ({fmtInt(u.volTrips)} trips).</p>
    </>
  );
}

